/**
 * Auth Store -- Xyden Leadership Assessment Platform
 * Step 6F Green Phase (Zustand)
 *
 * Note: Tests mock this store via vi.mock('@/stores/authStore').
 * This implementation is used in production mode.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '../types';

interface AuthStoreState {
  user: User | null;
  token: string | null;
  sessionId: string | null;
  /** Reason the user was logged out (e.g. "session expired"). Shown
   *  once on the login page and then cleared. */
  logoutReason: string | null;
  /** When true, a blocking "session kicked" dialog is shown globally.
   *  Set when another login supersedes this session — the user must
   *  acknowledge before being sent to the login page. */
  sessionKicked: boolean;
  isAuthenticated: boolean;
  /** True while the initial /api/auth/me check is in progress */
  isAuthLoading: boolean;
  /** True once the initial auth check has completed (success or failure) */
  isAuthResolved: boolean;
  logout: (reason?: string) => void;
  /** Show the "session kicked" dialog without immediately logging out. */
  setSessionKicked: () => void;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  setSessionId: (sessionId: string | null) => void;
  clearLogoutReason: () => void;
  setAuthResolved: (resolved: boolean) => void;
  setAuthLoading: (loading: boolean) => void;
  /** Reset store to initial state -- used by AppProviders on mount for test isolation */
  reset: () => void;
}

const initialState = {
  user: null,
  token: null,
  sessionId: null,
  logoutReason: null,
  sessionKicked: false,
  isAuthenticated: false,
  isAuthLoading: false,
  isAuthResolved: false,
};

export const useAuthStore = create<AuthStoreState>()(
  persist(
    (set) => ({
      ...initialState,

      logout: (reason?: string) => {
        set({
          user: null,
          token: null,
          sessionId: null,
          sessionKicked: false,
          isAuthenticated: false,
          logoutReason: reason ?? null,
        });
      },

      setSessionKicked: () => {
        set({ sessionKicked: true });
      },

      setUser: (user: User | null) => {
        set({ user, isAuthenticated: user !== null });
      },

      setToken: (token: string | null) => {
        set({ token });
      },

      setSessionId: (sessionId: string | null) => {
        set({ sessionId });
      },

      clearLogoutReason: () => {
        set({ logoutReason: null });
      },

      setAuthResolved: (resolved: boolean) => {
        set({ isAuthResolved: resolved });
      },

      setAuthLoading: (loading: boolean) => {
        set({ isAuthLoading: loading });
      },

      reset: () => {
        set({
          user: null,
          isAuthenticated: false,
          isAuthLoading: false,
          isAuthResolved: false,
          sessionKicked: false,
        });
      },
    }),
    {
      name: 'xyden-auth',
      partialize: (state) => ({
        token: state.token,
        sessionId: state.sessionId,
        logoutReason: state.logoutReason,
      }),
    }
  )
);
